import {
    IProductListAPIResponse,
    SortOrders,
    IDownloadProductListPayload,
    IProductListAPIPostBody,
    OrderTrackerDownloadFileTypes,
    DataViewFieldsMapping,
    ISortParam,
    ProductSortFieldsMapping,
    ISelectedProductOrderFilters,
    ITableConfig,
    ProductOrderFilters,
    SelectedFiltersInitialValues,
    ITableColumn,
    OrderListOriginalColumns,
} from '@telstra-connect/order-tracker/state/product-order-list';
import {
    DateFormat,
    formatDateWithYear,
} from '@telstra-connect/utils/data/date-helpers';
import {
    IProductOrder
} from '../../../../applications/order-tracker/config/order-tracker-new-common/types';
import { ILoadProductOrdersProps, IState } from './product-order-list/type';
import { IOrderDetailsStateResponse } from '@telstra-connect/order-tracker/state/order-details';
import { OrderSource } from '@telstra-connect/mobility-pex/type';

export const OrderTrackerV3BaseUrl = '/order-tracker/v3/orders';

export const formatProductsResponse = (
    data: IProductListAPIResponse
): IProductListAPIResponse => {
    const { products, totalProducts } = data;
    return {
        products: formatProducts(products),
        totalProducts,
    };
};

export const formatOrderSummaryResponse = (
    data: IOrderDetailsStateResponse
): IOrderDetailsStateResponse => {
    const { orderSummary, columns, pageSize, sourceSystem } = data;
    if (orderSummary) {
        // Format order completed and estimated completion dates
        orderSummary.orderCompletedDate = formatDateField(
            orderSummary.orderCompletedDate
        );
        orderSummary.estimatedCompletionDate = formatDateField(
            orderSummary.estimatedCompletionDate
        );
    }
    if (orderSummary?.products?.length) {
        // Format dates for each product in the order summary
        orderSummary.products = formatProducts(orderSummary.products);
    }
    return { orderSummary, columns, pageSize, sourceSystem };
};

const formatProducts = (products: IProductOrder[]): IProductOrder[] => {
    products?.forEach((product) => {
        if (product?.estimatedCompletionDate) {
            product.estimatedCompletionDate = formatDateField(
                product.estimatedCompletionDate
            );
        }
        if (product?.upcomingAppointmentDate) {
            product.upcomingAppointmentDate = formatDateField(
                product.upcomingAppointmentDate
            );
        }
        if (product?.appointmentDetails?.date) {
            product.appointmentDetails.date = formatDateField(
                product.appointmentDetails.date
            );
        }
        if (product?.equipmentDetails?.deliveryDate) {
            product.equipmentDetails.deliveryDate = formatDateField(
                product.equipmentDetails.deliveryDate
            );
        }
    });
    return products;
};

const formatDateField = (field: string | undefined) =>
    field ? formatDateWithYear(field, DateFormat.DEFAULT_DATE) : field;

export const configureProductOrderPayload = (
    state: IState,
    params?: ILoadProductOrdersProps
): {
    payload: IProductListAPIPostBody;
    tableConfig: ITableConfig;
    selectedFilters: ISelectedProductOrderFilters;
    searchText: string;
    sortParam: ISortParam;
    selectedColumns: ITableColumn[];
} => {
    const { productOrderList } = state.orderTracker;
    const { tableConfig, selectedFilters, searchText, sortParam: sortState } = productOrderList;

    const pageNumber = params?.pageNumber ?? tableConfig.currentPage;
    const pageSize = params?.pageSize ?? tableConfig.pageSize;
    const searchBar =
        typeof params?.searchText === 'string' ? params.searchText : searchText;

    const updatedSelectedFilters: ISelectedProductOrderFilters =
        configureSelectedFilters(selectedFilters, params);
    const sortData = configureSortParams(params?.sortParam, sortState);
    return {
        payload: {
            pageNumber,
            pageSize,
            ...(searchBar && { searchBar }),
            ...(!!updatedSelectedFilters[ProductOrderFilters.productFilter]
                ?.length && {
                products:
                    updatedSelectedFilters[ProductOrderFilters.productFilter],
            }),
            ...(!!updatedSelectedFilters[ProductOrderFilters.statusFilter]
                ?.length && {
                orderStatus:
                    updatedSelectedFilters[
                        ProductOrderFilters.statusFilter
                    ].join(','),
            }),
            ...(!!updatedSelectedFilters[ProductOrderFilters.customerFilter]
                ?.length && {
                customers:
                    updatedSelectedFilters[ProductOrderFilters.customerFilter],
            }),
            ...(!!updatedSelectedFilters[ProductOrderFilters.siteAddressFilter]
                ?.length && {
                siteAddresses: updatedSelectedFilters[
                    ProductOrderFilters.siteAddressFilter
                ],
            }),
            ...(!!sortData && sortData),
        },
        tableConfig: { currentPage: pageNumber, pageSize },
        selectedFilters: updatedSelectedFilters,
        searchText: searchBar,
        sortParam: params?.sortParam,
        selectedColumns: params?.selectedColumns
            ? OrderListOriginalColumns.filter((value: ITableColumn) =>
                  params.selectedColumns.includes(value.accessor)
              )
            : null,
    };
};

export const configureDownloadProductOrderPayload = (
    state: IState,
    fileType: OrderTrackerDownloadFileTypes
): { payload: IDownloadProductListPayload } => {
    const { payload } = configureProductOrderPayload(state);
    const { selectedColumns } = state.orderTracker.productOrderList;

    const { sortParam } = state.orderTracker.productOrderList;
    const sortData = configureSortParams(undefined, sortParam);
    return {
        payload: {
            fileType,
            dataView: selectedColumns.map(
                (column) => DataViewFieldsMapping[column.accessor]
            ),
            ...(!!payload.orderStatus && { orderStatus: payload.orderStatus }),
            ...(!!payload.products?.length && { products: payload.products }),
            ...(!!payload.siteAddresses?.length && {
                siteAddresses: payload.siteAddresses,
            }),
            ...(!!payload.customers?.length && {
                customers: payload.customers,
            }),
            ...(!!payload.searchBar && { searchBar: payload.searchBar }),
            ...(!!sortData && sortData),
        },
    };
};

const configureSelectedFilters = (
    selectedFilters: ISelectedProductOrderFilters,
    params: ILoadProductOrdersProps
): ISelectedProductOrderFilters => {
    const newSelectedFilter = params?.newSelectedFilter;
    const removeSelectedFilter = params?.removeSelectedFilter;
    const clearAllFilters: boolean = !!params?.clearAllFilter;
    const allUpdatedFilters = params?.allUpdatedFilters;
    return {
        ...selectedFilters,
        ...allUpdatedFilters,
        ...(newSelectedFilter && {
            [newSelectedFilter.filterKey]: newSelectedFilter.filterValue,
        }),
        ...(clearAllFilters && SelectedFiltersInitialValues),
        ...(removeSelectedFilter && {
            [removeSelectedFilter.filterKey]: !!selectedFilters[
                removeSelectedFilter.filterKey
            ]
                ? selectedFilters[removeSelectedFilter.filterKey].filter(
                      (item: string) =>
                          item !== removeSelectedFilter.filterValue
                  )
                : [],
        }),
    };
};

const configureSortParams = (
    /** The sorting parameters provided in the current request, may be undefined. */
    sortParam: ISortParam | undefined,
    /** The currently active sorting state from the Redux store. */
    sortState: ISortParam
): { sortBy: string; sortOrder: SortOrders } | null => {
    // Case 1: The sorting parameters provided in the current request is undefined
    if (!sortParam) {
        // Case 1.1: Use sorting preference already stored in the `sortState` (Redux).
        return sortState?.sortBy && sortState?.sortOrder
            ? {
                  sortBy: ProductSortFieldsMapping[sortState.sortBy],
                  sortOrder: SortOrders[sortState.sortOrder],
              }
            // Case 1.2: No sorting preference stored in the `sortState` (Redux).
            : null; // It simply means No Sorting.
    }
    // Case 2: If `sortParam` is defined but doesn't have `sortBy` property or valid order
    if (!sortParam.sortBy || !sortParam.sortOrder) {
        return null; // It simply means No Sorting.
    }
    // Case 3: If `sortParam` has a `sortBy` value, we use the sorting from the current request.
    return {
        sortBy: ProductSortFieldsMapping[sortParam.sortBy],
        sortOrder: SortOrders[sortParam.sortOrder],
    };
};

export const hasNoFiltersSelected = (
    payload: IProductListAPIPostBody
): boolean => {
    const { pageNumber, pageSize, ...rest } = payload;
    return Object.keys(rest).length === 0;
};

export const hasNoSelectedFilterAndSearch = (state: IState) => {
    const { searchText, selectedFilters } = state.orderTracker.productOrderList;
    const hasSearchText: boolean = searchText?.trim().length > 0;
    const hasSelectedFilter: boolean = !!(
        selectedFilters &&
        Object.values(selectedFilters).find(
            (selectedValue) => selectedValue?.length > 0
        )
    );
    return !hasSearchText && !hasSelectedFilter;
};

export const isSourceMpex = (sourceSystem: string): boolean => {
    if (!sourceSystem) {
        return false;
    }
    return sourceSystem.toLowerCase() === OrderSource.mpex;
}

const haveSameValues = (list1: string[], list2: string[]): boolean => {
    if (list1.length !== list2.length) { return false; }
    const sorted1 = [...list1].sort();
    const sorted2 = [...list2].sort();
    return sorted1.every((value, index) => value === sorted2[index]);
};

export const noChangeInFilter = (
    filterKey: ProductOrderFilters,
    filterValue: string[],
    state: IState
): boolean => {
    const alreadySelectedFilters =
        state.orderTracker.productOrderList.selectedFilters;
    return haveSameValues(alreadySelectedFilters[filterKey], filterValue);
};

export const noChangeInFilters = (
    allUpdatedFilters: Record<ProductOrderFilters, string[]>,
    state: IState
): boolean => {
    const alreadySelectedFilters =
        state.orderTracker.productOrderList.selectedFilters;
    const filterKeys = Object.keys(
        alreadySelectedFilters
    ) as ProductOrderFilters[];
    return !filterKeys.some(
        (filterKey) =>
            !haveSameValues(
                alreadySelectedFilters[filterKey],
                allUpdatedFilters[filterKey]
            )
    );
};
